import React, { useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { MMEC_BRAND_COLORS } from '../utils/mmecBrandColors';
import { APP_VERSION_LABEL } from '../config/appVersion';

interface LegalDisclaimerProps {
  onAccept: () => void;
  onDeny: () => void;
}

const LegalDisclaimer: React.FC<LegalDisclaimerProps> = ({ onAccept, onDeny }) => {
  useTheme();
  const { language } = useLanguage();
  const [isVisible, setIsVisible] = useState(true);

  const handleAccept = () => {
    setIsVisible(false);
    onAccept();
  };

  const handleDeny = () => {
    setIsVisible(false);
    onDeny();
  };

  if (!isVisible) return null;

  const translations = {
    welcome: language === 'ar' ? 'مرحباً بك في منصة MMEC' : 'Welcome to MMEC Platform',
    subtitle: language === 'ar' ? 'أدوات التحليل المالي المتقدمة' : 'Advanced Financial Analysis Tools',
    accept: language === 'ar' ? 'أوافق وأستمر' : 'Accept & Continue',
    deny: language === 'ar' ? 'رفض والخروج' : 'Decline & Exit',
    legalNotice: language === 'ar' 
      ? 'تنبيه قانوني: جميع الأدوات والتحليلات المقدمة على هذه المنصة هي لأغراض تعليمية وتحليلية فقط. MMEC لا تتحمل أي مسؤولية عن القرارات المالية التي يتخذها المستخدمون. يقر المستخدمون بأنهم يستخدمون هذه المنصة على مسؤوليتهم الخاصة وأن جميع الاستثمارات تنطوي على مخاطر.'
      : 'Legal Notice: All tools and analyses provided on this platform are for educational and analytical purposes only. MMEC does not take any responsibility for financial decisions made by users. Users acknowledge that they use this platform at their own risk and that all investments involve risk.',
    disclaimer: language === 'ar' 
      ? 'هذه المنصة لا تشكل نصيحة مالية أو استثمارية. يرجى استشارة مستشار مالي مؤهل قبل اتخاذ أي قرارات استثمارية.'
      : 'This platform does not constitute financial or investment advice. Please consult with a qualified financial advisor before making any investment decisions.'
  };

  const containerClass = 'fixed inset-0 z-50 flex items-center justify-center p-4';

  const cardClass = 'max-w-md w-full';

  const buttonClass = 'px-6 py-3 rounded-lg font-medium';

  return (
    <div className={containerClass} style={{ background: 'var(--void)', fontFamily: 'var(--font-ui)' }}>
      <div
        className={cardClass}
        style={{ background: 'var(--elev)', border: '1px solid var(--hair)', borderRadius: 'var(--radius)', boxShadow: 'var(--lift-3)' }}
      >
        {/* Header */}
        <div className="text-center p-6" style={{ borderBottom: '1px solid var(--hair)' }}>
          <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center text-2xl"
               style={{ backgroundColor: MMEC_BRAND_COLORS.orange }}>
            📊
          </div>
          <h1 className="mb-2" style={{ fontSize: 18, fontWeight: 600, color: 'var(--txt)' }}>
            {translations.welcome}
          </h1>
          <p style={{ fontSize: 13, color: 'var(--muted)' }}>
            {translations.subtitle}
          </p>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Legal Notice */}
          <div className="p-4 mb-6" style={{ background: 'var(--elev2)', border: '1px solid var(--hair)', borderRadius: 8 }}>
            <p className="leading-relaxed" style={{ fontSize: 12, color: 'var(--txt-2)' }}>
              {translations.legalNotice}
            </p>
          </div>

          {/* Additional Disclaimer */}
          <div
            className="p-3 mb-6"
            style={{
              background: 'var(--down-dim)',
              border: '1px solid color-mix(in srgb, var(--down) 40%, transparent)',
              borderRadius: 8,
            }}
          >
            <p className="leading-relaxed" style={{ fontSize: 12, color: 'var(--down)' }}>
              {translations.disclaimer}
            </p>
          </div>

          {/* Buttons */}
          <div className="flex flex-col" style={{ gap: 12 }}>
            <button
              onClick={handleAccept}
              className={buttonClass}
              style={{ background: 'var(--acc)', color: 'var(--void)', fontSize: 13, transition: 'opacity var(--dur-control) var(--ease)' }}
            >
              {translations.accept}
            </button>
            
            <button
              onClick={handleDeny}
              className={buttonClass}
              style={{
                background: 'var(--down-dim)',
                color: 'var(--down)',
                border: '1px solid color-mix(in srgb, var(--down) 40%, transparent)',
                fontSize: 13,
                transition: 'opacity var(--dur-control) var(--ease)',
              }}
            >
              {translations.deny}
            </button>
          </div>
        </div>

        {/* Footer */}
        <div
          className="text-center p-4"
          style={{ borderTop: '1px solid var(--hair)', background: 'var(--base)', borderRadius: '0 0 var(--radius) var(--radius)' }}
        >
          <p style={{ fontSize: 11, color: 'var(--muted-2)' }}>
            MMEC RTL Market Scanner v{APP_VERSION_LABEL}
          </p>
        </div>
      </div>
    </div>
  );
};

export default LegalDisclaimer;
